import { ReactNode } from "react";
import { Navigate, Routes, Route, useNavigate } from "react-router-dom";
import { Security, LoginCallback } from "@okta/okta-react";
import { getOktaAuth, isOktaEnabled } from "./auth/oktaConfig";
import { RequireAuth } from "./auth/RequireAuth";
import { AppLayout } from "./components/layout/AppLayout";
import { Home } from "./pages/Home";
import { Forbidden } from "./pages/Forbidden";
import { ExportNew } from "./pages/ExportNew";
import { ExportsList } from "./pages/ExportsList";
import { ExportDetail } from "./pages/ExportDetail";
import { SearchEnvsList } from "./pages/SearchEnvsList";
import { SearchEnvDetail } from "./pages/SearchEnvDetail";
import { AdminArchive } from "./pages/AdminArchive";
import { ArchiveDescription } from "./pages/ArchiveDescription";
import { ArchiveYear } from "./pages/ArchiveYear";
import { ClusterActivity } from "./pages/ClusterActivity";
import { SchemaReference } from "./pages/SchemaReference";

// Top-level app routes. ThemeProvider + CssBaseline live one level up
// in main.tsx so the brand theme wraps everything (including Okta's
// own redirect screens). The Okta <Security> provider only wraps when
// Okta is configured; otherwise RequireAuth no-ops and the app runs
// unauthenticated against the open-mode backend.
//
// By the time this component renders, <PortalConfigProvider> has
// already loaded /api/portal-config and (if Okta is enabled) wired
// the module-level OktaAuth holder via setOktaAuthFromConfig. So
// both getOktaAuth() and isOktaEnabled() reflect runtime config,
// not bundle-baked env vars.

const wrap = (page: ReactNode) => (
  <RequireAuth>
    <AppLayout>{page}</AppLayout>
  </RequireAuth>
);

export function App() {
  const navigate = useNavigate();
  const restoreOriginalUri = async (_oktaAuth: unknown, originalUri: string) => {
    navigate(originalUri || "/", { replace: true });
  };

  const tree = (
    <Routes>
      <Route path="/login/callback" element={<LoginCallback />} />
      <Route path="/403" element={<Forbidden />} />
      <Route path="/" element={wrap(<Home />)} />
      <Route path="/exports" element={wrap(<ExportsList />)} />
      <Route path="/exports/new" element={wrap(<ExportNew />)} />
      <Route path="/exports/:workflowId" element={wrap(<ExportDetail />)} />
      <Route path="/search-envs" element={wrap(<SearchEnvsList />)} />
      <Route path="/search-envs/:envId" element={wrap(<SearchEnvDetail />)} />
      <Route path="/activity" element={wrap(<ClusterActivity />)} />
      <Route path="/schema" element={wrap(<SchemaReference />)} />
      <Route path="/admin" element={<Navigate to="/admin/archive" replace />} />
      <Route path="/admin/archive" element={wrap(<AdminArchive />)} />
      <Route path="/admin/description" element={wrap(<ArchiveDescription />)} />
      <Route path="/admin/description/:year" element={wrap(<ArchiveYear />)} />
    </Routes>
  );

  const oktaAuth = getOktaAuth();
  return isOktaEnabled() && oktaAuth ? (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {tree}
    </Security>
  ) : (
    tree
  );
}
